import dayjs from "dayjs";
import { groupBy, orderBy, uniqBy } from "lodash";
import { Item__Atividades, Item__Atividades__andamentos, ItemPropriedades, typeResumoIncomp } from "../../../../../../../services/atividades/GET";
import { Item__EquipeCliente } from "../../../../../../../services/Equipe/GET";
import { leadZero } from "../../../../../../../utils";

/**gera intervalo de datas entre as atividades para formar a timeline  */
function gerarIntervaloEntreDatas(data_incio: string, data_fim: string) {

    const data_inicial = new Date(data_incio);
    const data_final = new Date(data_fim);
    const resultado = [];

    if (data_final <= data_inicial) {
        return [];
    }

    let data_atual = new Date(data_inicial);

    while (data_atual <= data_final) {
        resultado.push(data_atual.toISOString().split('T')[0]);
        data_atual.setDate(data_atual.getDate() + 1);
    }

    return resultado;
}

//#region GERAR TIMELINE CARDS
export type typeEstruturaCard = {
    data_picker?: string;
    data_base?: {
        date: string;
        dia_zero: boolean;
    };
    possui_atividade?: boolean;
    is_dia_zero?: boolean;
    atividades?: typeAtvEstrutura[];
}

/**lista as atividades na forma de timeline. (só é utilizado agora como base para criar a timeline compacta) */
export function gerarTimelineCards(lista_atividades: typeAtvEstrutura[]): typeEstruturaCard[] {

    /**estrutura base para a timeline */
    let estrutura_lista_atividades: { date: string, dia_zero: boolean }[] = (lista_atividades ?? []).map(({ data_prevista, dias }) => {
        return {
            date: dayjs(data_prevista).format("YYYY-MM-DD"),
            dia_zero: String(dias) === '0',
        }
    });

    /**lista as datas ordenadas por data 'asc' */
    let ordenar_datas = orderBy(estrutura_lista_atividades, 'date', ['asc']);

    /**encontra a data zero (geralmente data do plantio) */
    let possui_dia_zero = estrutura_lista_atividades.find(item => item.dia_zero === true);

    /**usado para */
    let primeira_data = ordenar_datas?.[0]?.date; //primeira data da lista de atividades
    let ultima_data = ordenar_datas?.[ordenar_datas.length - 1]?.date; //ultima data da lista de atividades
    let data_no_meio = ordenar_datas?.[Math.ceil(ordenar_datas.length / 2) - 1]; //data que fica na metade (no meio) da lista de atividades

    /**intervalos usados para gerar timeline. É tratado para listar as atividades sem cortar nenhuma, 
     * de modo que todas estejam visívies na timeline, essa separação é necessária nos casos onde o intervalo entre a primeira data e a ultima seja muito longa (acima de 45 dias)
     * 
     * `também é usado para obter o dia base, para saber qual data será usada como referencia nos casos onde não houver dia zero`*/
    let intervalo_primeiro_meio = gerarIntervaloEntreDatas(primeira_data, (possui_dia_zero ? possui_dia_zero.date : data_no_meio?.date)).length;
    let intervalo_meio_ultimo = gerarIntervaloEntreDatas((possui_dia_zero ? possui_dia_zero.date : data_no_meio?.date), ultima_data).length;
    let intervalo_primeiro_ultimo = gerarIntervaloEntreDatas(primeira_data, ultima_data).length;

    /**retorna a data base (data referencia usadada para gerar as datas da timline) 
     * 
     * `default:` se houver dia zero, ela será a `data base` (via de regra só deve haver 1 dia zero);
     * 
     * se não houver `dia zero`, a  atividade que estiver no meio da lista será a `data base`;
     * 
     * se não houver nenhuma data, a data atual será usada como data base (isso acaba não sendo utilizado, pois quando não há atividades o componente de timline não é renderizado)
    */
    function encontrarDiaBase() {

        /**se não houver atividades retorna a data atual como dia base */
        if (ordenar_datas.length === 0) {
            return dayjs().format('YYYY-MM-DD');
        }

        /**encontra o dia zero */
        let find_dia_zero = estrutura_lista_atividades.find(item => item.dia_zero === true);

        /**se houver dia zero, retorna a data dele como data base */
        if (find_dia_zero) {
            return find_dia_zero.date;
        }

        /**se não houver dia zero, retorna a data da atividade que estiver no meio da lista (lista é ordenada)*/
        let data_no_meio = ordenar_datas?.[Math.ceil(ordenar_datas.length / 2) - 1];

        return data_no_meio?.date;

    }

    /**armazena a data base. `exemplo: 2025-10-30` */
    let dia_base = encontrarDiaBase();

    /**gera as datas da timeline de modo que todas as atividaeds estejam sempre visiveis na timeline lateral.
     * gerando dinamicamente a quantidade de datas necessárias para desenhar a timeline.
     * 
     * no caso são as bolinhas da timeline, onde cada bolinha representa um dia (uma data)
     */
    function gerarIntervaloDeDatas(dia_zero: string) {

        /**retorna a quantidade de dias que deverá ser gerada, para garantir que seja gerada o suficiente para tornar todas as atvs visíveis */
        function obterDiasAntesEDepois() {

            let metade: number = Math.round(intervalo_primeiro_ultimo / 2);
            let maior_diferenca: number = intervalo_primeiro_meio > intervalo_meio_ultimo ? intervalo_primeiro_meio : intervalo_meio_ultimo;
            let menor_diferenca: number = intervalo_primeiro_meio < intervalo_meio_ultimo ? intervalo_primeiro_meio : intervalo_meio_ultimo;

            if (metade <= 22) {
                let diferenca = (maior_diferenca <= 22 ? 50 : maior_diferenca);
                return menor_diferenca <= 22 ? diferenca : menor_diferenca
            }

            return maior_diferenca <= 22 ? 50 : maior_diferenca;
        }

        const dias_antes_e_depois: number = obterDiasAntesEDepois()
        const data_base = new Date(dia_zero);

        const array_datas = [];

        for (let i = -dias_antes_e_depois; i <= dias_antes_e_depois; i++) {
            const novaData = new Date(data_base);
            novaData.setDate(data_base.getDate() + i);

            array_datas.push(novaData.toISOString().split('T')[0]);
        }

        return array_datas;
    };

    /**estrutura da listagem dos cards */
    let montar_timeline: typeEstruturaCard[] = (gerarIntervaloDeDatas(dia_base) ?? []).map((data: string) => {
        return {
            data_picker: data,
            data_final: (lista_atividades ?? []).find(i => i.data_prevista === data)?.data_final_prevista ?? data,
            tipo_referencia: (lista_atividades ?? []).find(i => i.data_prevista === data)?.tipo_referencia ?? 3,
            data_base: possui_dia_zero ?? null,
            possui_atividade: ((ordenar_datas ?? []).some(item => item.date === data)) ?? false,
            is_dia_zero: ((ordenar_datas ?? []).find(item => item.date === data))?.dia_zero ?? false,
            atividades: lista_atividades.filter(i => i.data_prevista === data)
        }
    });

    /**se gerar muitas datas (muitas bolinhas) a ponto de não ficar visível, remove as datas que não possui atvs para garantir que as datas com atvs estejam visiveis*/
    let remover_excessos: typeEstruturaCard[] = intervalo_primeiro_ultimo <= 45 ? montar_timeline : montar_timeline.filter(item => item.data_picker >= primeira_data && item.data_picker <= ultima_data)

    return remover_excessos ?? [];
};
////#endregion

//#region GERAR TIMELINE COMPACTA
/**Gera a timeline apenas com as atividades (ou seja, sem as bolinhas, lista apenas os cards) */
export function gerarTimelineCompacta(lista_atividades: typeAtvEstrutura[]) {

    /**timeline normal */
    let timeline_Gerada = gerarTimelineCards(lista_atividades ?? []);
    /**remove as bolinhas e lista apenas datas com atividades */
    let apenas_data_com_atv = (timeline_Gerada ?? []).filter(item => item.possui_atividade).map(item => { return { intervalo: 0, ...item } });

    /**lista as atividades, incluindo a quantide de dias entre uma atividade e outra*/
    function compactar(atividades_com_datas: any[]) {

        /**retorna a data de referencia que será usada para contar a quantidade de dias entre uma atividade e outra;
         * 
         * `data de ínicio` quando a próxima data é antes da dta média
         * 
         * `data média` é o pdrão
         * 
         * `data final` quando o intervalo entre uma e outra é menor ou igual á 2 dias
         */
        function obterDataIntervalo(item: any, proxima_data?: any) {
            let duracao: number = Math.abs(dayjs(item.data_picker).diff(dayjs(item.data_final), 'day'));
            let media_dias: number = Math.ceil(duracao / 2);
            let data_media = dayjs(item.data_picker).add(media_dias, 'day');

            if (duracao <= 2) {
                return dayjs(item.data_final);
            }

            if (dayjs(proxima_data).isBefore(data_media)) {
                return dayjs(item.data_picker);
            }

            return dayjs(data_media);
        }

        /**retorna o `cod_referencia` usado para saber qual data se basear*/
        function obterTipoRefIntervalo(item: any, proxima_data?: any) {
            let duracao: number = Math.abs(dayjs(item.data_picker).diff(dayjs(item.data_final), 'day'));
            let media_dias: number = Math.ceil(duracao / 2);
            let data_media = dayjs(item.data_picker).add(media_dias, 'day');

            if (duracao <= 2) {
                return 3;
            }

            if (dayjs(proxima_data).isBefore(data_media)) {
                return 1;
            }

            return 2;
        }

        /**adiciona o intervalo de dias e o cod_referencia na estrutura da listagem de atividades */
        let montar_datas = atividades_com_datas.map((item, index) => {

            let proxima_data = atividades_com_datas?.[index + 1]?.data_picker;

            const diferenca_dias = proxima_data ? Math.abs(dayjs(proxima_data).diff(obterDataIntervalo(item, proxima_data), 'day')) : -1;

            return {
                ...item,
                intervalo: diferenca_dias,
                tipo_referencia: obterTipoRefIntervalo(item, proxima_data),
                possui_atividade: false
            }
        });

        let ordernar = orderBy(([...atividades_com_datas, ...montar_datas]), ['data_picker', 'intervalo'], ['asc'])

        return (ordernar ?? []).filter(i => i.intervalo !== -1);
    }

    return compactar(apenas_data_com_atv) ?? [];
};
////#endregion


//#region OBTER FINALIDADES DIRETA (EXECUTADA AVULSA)
//TYPES
export type typeAndamentos = {
    cod_andamento: number,
    area: string,
    nome_operador: string,
    patrimonios: any[],
    insumos: any[],
    dados_andamentos: any,
    data_execucao?: string,
    data_inicio_planejado?: string,
    data_final_planejado?: string,
    propriedade_nome?: string,
    nome_talhao?: string,
}
export type typeFinalidades_executadas = {
    cod_finalidade: number,
    nome_finalidade: string,
    porcentagem_executada: number,
    area_total: string | number,
    area_executada: string | number,
    andamentos: typeAndamentos[]
}
export type typeResumoEstrutura = {
    resumo_planejadas: { finalidades: string, resumo: typeResumoIncomp[] }[],
    resumo_executadas: { finalidades: string, resumo: typeResumoIncomp[] }[],
    possui_alertas: boolean
}
export type typeAtvEstrutura = {
    cod_agrupado?: string,
    cod: number,
    cod_tipo?: number,
    cod_atividade: number,
    cod_referencia?: number,
    dias: number | string,
    data_prevista: string,
    data_final_prevista?: string,
    cod_operacao: number,
    titulo_operacao: string,
    qtd_atv_executada: number,
    porcentagem_executada: number,
    is_concluido: boolean,
    finalidades_executadas?: typeFinalidades_executadas[],
    dados_atividade: Item__Atividades | Item__Atividades[],
    tipo_atividade: 'planejada' | 'executada' | 'emergencia',
    resumo_compatibilidades: typeResumoEstrutura,
    intervalo?: number,
    cod_propriedade?: number;
    nome_propriedade?: string;
    cod_talhao?: number,
    ref_agrupar?: string,
    propriedades?: { nome_propriedade: string, cod_propriedade: number, posicao: number }[],
    talhoes?: { cod_propriedade: number, cod_talhao: number, nome_talhao: string, posicao: number, coordenadas: { latitude: number, longitude: number }[] }[],
    listar_primeiro?: number,
    porcentagens: { label: string, value: number, color: string }[],
    ocultar_plantio_ref_filtro_avancado?: boolean,
    tipo_referencia?: number,
    obs?: string;
    tipo_filtro: string;
    nome_talhao?: string;
    nome_subarea?: string;
    nome_subcard?: string;
};
/** Monta a estrutura de listagem de finalidades de atividade executada que não está vinculada a nenhuma atividade planejada
 * 
 * `cod_atividade` === `null`
*/
function obterFinalidadeDireta(dados_atv: Item__Atividades) {

    /**retorna as execuções da atividade executada avulsa */
    function obterExecucoes(andamentos: Item__Atividades__andamentos[]) {
        let soma_area = (andamentos ?? []).reduce((acc, item) => acc + Number(item?.area), 0);

        let execucoes = (andamentos ?? []).map((exe) => {
            return {
                cod_andamento: exe?.cod,
                area: exe.area,
                nome_operador: exe?.usuario_nome,
                patrimonios: exe.patrimonios,
                insumos: exe.insumos
            }
        })

        return {
            soma_area: soma_area,
            execucoes: execucoes
        }
    };

    let finalidades_direto = (dados_atv?.finalidades ?? []).map((item_finali) => {

        let area_total: number = Number(dados_atv?.area);
        let soma_areas: number = obterExecucoes(dados_atv?.andamentos)?.soma_area ?? 0;
        let porcentagem_concluida: number = (soma_areas * 100) / area_total;

        return {
            cod_finalidade: item_finali?.cod_finalidade,
            porcentagem_executada: Math.round(porcentagem_concluida),
            nome_finalidade: item_finali?.tipo_finalidade,
            area_total: dados_atv?.area,
            area_executada: obterExecucoes(dados_atv?.andamentos)?.soma_area ?? 0,
            andamentos: obterExecucoes(dados_atv?.andamentos)?.execucoes ?? []
        }
    });

    return finalidades_direto;
};
//#endregion


//#region OBTER FINALIDADES VINCULADAS
/**retorna a lista de finalidades de atividadeas executadas que estão vincualdas a um planejamento*/
function obterFinalidadesVinculadas(
    atividades_vinculadas: Item__Atividades[],
    dados_atv: Item__Atividades,
    lista_usuarios: Item__EquipeCliente[],
    lista_propriedades: ItemPropriedades[]
) {

    /**aditividades executadas que estão vinculadas a uma planejada */
    let vinculadas = (atividades_vinculadas ?? []).filter(item => item.cod_atividade === dados_atv.cod);

    /**retorna o nome do operador */
    function obterNomeOperador(cod_usuario: number, nome_operador_andamento?: string | null) {
        if (nome_operador_andamento) {
            return nome_operador_andamento;
        }
        let find_usuario = (lista_usuarios ?? [])?.find(item => item.cod_func === cod_usuario)?.usuario_nome ?? null;
        return find_usuario ?? '???';
    }

    /**retorna o nome do talhão */
    function obterNomeTalhao(cod_talhao: number) {
        //@ts-ignore
        let lista_talhoes = (lista_propriedades ?? []).map(item => item.talhao).flat();
        let find_talhao = (lista_talhoes ?? [])?.find(item => item.cod === cod_talhao)?.nome ?? null
        return find_talhao ?? '???'
    }

    /**agrupa as execuções por tipo de finalidade */
    function agruparPorFinalidades(): { cod_finalidade: number, nome_finalidade: string, atividades_executadas: any }[] {
        const agrupadoPorFinalidades = {};

        for (const atividade of vinculadas) {

            for (const finalidade of atividade.finalidades) {
                const chave = finalidade.cod_finalidade;

                agrupadoPorFinalidades[chave] ??= {
                    cod_finalidade: finalidade.cod_finalidade,
                    nome_finalidade: finalidade.tipo_finalidade,
                    atividades_executadas: [],
                };

                agrupadoPorFinalidades[chave].atividades_executadas?.push({ ...atividade });
            }
        }

        return Object.values(agrupadoPorFinalidades);
    }

    let agrupar_por_finalidade: { cod_finalidade: number, nome_finalidade: string, atividades_executadas: any }[] = vinculadas.length > 0 ? agruparPorFinalidades() : [];

    let estrutura_finalidades = (agrupar_por_finalidade ?? []).map(({ cod_finalidade, nome_finalidade, atividades_executadas }) => {

        //@ts-ignore
        let mesclar_andamentos = atividades_executadas.map(i => i.andamentos.map(j => ({ ...j, data_execucao: i.data_final, propriedade_nome: i.propriedade_nome, cod_talhao: i.cod_talhao }))).flat();
        let area_total: number = Number(dados_atv?.area);
        let soma_areas: number = (mesclar_andamentos ?? []).reduce((acc: number, item: any) => acc + Number(item?.area), 0);
        let porcentagem_concluida: number = (soma_areas * 100) / area_total;

        return {
            cod_finalidade: cod_finalidade,
            nome_finalidade: nome_finalidade,
            porcentagem_executada: Math.round(porcentagem_concluida) ?? 0,
            area_total: dados_atv?.area,
            area_executada: soma_areas,
            andamentos: (mesclar_andamentos ?? []).map((anda) => {
                return {
                    cod_andamento: anda?.cod,
                    area: anda?.area,
                    cod_usuario: anda?.cod_usuario,
                    nome_operador: obterNomeOperador(anda?.cod_usuario, anda?.operador_nome),
                    patrimonios: anda?.patrimonios,
                    insumos: anda?.insumos,
                    dados_andamentos: anda,
                    data_inicio_planejado: dados_atv?.data_inicio,
                    data_final_planejado: dados_atv?.data_final,
                    data_execucao: anda?.data_execucao,
                    propriedade_nome: anda?.propriedade_nome,
                    cod_talhao: anda?.cod_talhao,
                    nome_talhao: obterNomeTalhao(anda?.cod_talhao),
                    created_at: anda?.create_time,
                    created_by: anda?.create_user,
                    updated_at: anda?.update_time,
                    updated_by: anda?.update_user,
                }
            })

        }
    })

    return estrutura_finalidades ?? [];
}
//#endregion

//#region RESUNMO DE COMPATIBILIDADES
/**retorna as incompatibilidades encontradas na atividade */
function obterResumosCompatibilidades(dados_atv: Item__Atividades, atividades_vinculadas: Item__Atividades[]): typeResumoEstrutura {

    let card_resumo = dados_atv?.cod_tipo === 1 ? {
        finalidades: (dados_atv?.finalidades)?.map(i => i.tipo_finalidade),
        resumo: dados_atv?.resumo_compatibilidade ?? []
    } : null;

    let finalidades_card_resumo = (card_resumo?.finalidades ?? []).map(finaItem => ({
        finalidades: finaItem,
        resumo: card_resumo.resumo
    }));

    let vinculadas = (atividades_vinculadas ?? []).filter(item => item.cod_atividade === dados_atv.cod)?.map((item) => {
        return {
            finalidades: (item?.finalidades)?.map(i => i.tipo_finalidade),
            resumo: item?.resumo_compatibilidade ?? []
        }
    });

    //@ts-ignore
    let finalidades_vinculadas = (vinculadas ?? []).flatMap(item =>
        (item?.finalidades ?? []).map(finaItem => ({
            finalidades: finaItem,
            resumo: item.resumo
        }))
    );

    function agruparPorFinalidades(fina_vinculadas: any[]): { finalidades: string, resumo: typeResumoIncomp[] }[] {
        const mergeMap = {};

        for (let item of fina_vinculadas) {
            mergeMap[item.finalidades] ??= { finalidades: item.finalidades, resumo: [] };
            mergeMap[item.finalidades].resumo.push(...item.resumo);
        }

        return Object.values(mergeMap);
    };

    let resumo_planejada = (finalidades_card_resumo ?? []).filter(item => (item?.resumo)?.length > 0);
    let resumo_executadas = (agruparPorFinalidades(finalidades_vinculadas) ?? []).filter(item => (item?.resumo)?.length > 0);

    let estrutura: typeResumoEstrutura = {
        resumo_planejadas: resumo_planejada ?? [],
        resumo_executadas: resumo_executadas ?? [],
        possui_alertas: resumo_planejada?.length > 0 || resumo_executadas?.length > 0
    }

    return estrutura;
}
//#endregion

/**retorna em porcentagem se as atividades executadas da atividade estão `adiantada`, `no prazo`, `atrasada` 
 * 
 * usado para o gráfico de donut no card
*/
function obterPorcentagensDePrazo(dados_atv: Item__Atividades, atividades_vinculadas: Item__Atividades[]): { label: string, value: number, color: string }[] {

    let data_prazo_inicial = dados_atv?.data_inicio; //formato data `exemplo: 30-10-2025`
    let data_prazo_final = dados_atv?.data_final ?? dados_atv?.data_inicio;//formato data `exemplo: 30-10-2025`
    /**atividade executadas vinculada a atividade planejada informada */
    let atividades_executadas = (atividades_vinculadas ?? []).filter(i => i.cod_atividade === dados_atv?.cod);

    let antes_prazo = ((atividades_executadas ?? []).filter(item => item.data_final < data_prazo_inicial && item.data_final < data_prazo_final) ?? []);
    let dentro_prazo = ((atividades_executadas ?? []).filter(item => item.data_final >= data_prazo_inicial && item.data_final <= data_prazo_final) ?? []);
    let depois_prazo = ((atividades_executadas ?? []).filter(item => item.data_final > data_prazo_final && item.data_final > data_prazo_inicial) ?? []);

    //@ts-ignore
    let contagem_antes_prazo: number = (antes_prazo ?? [])?.map(i => i.andamentos)?.flat()?.length;
    //@ts-ignore
    let contagem_dentro_prazo: number = (dentro_prazo ?? [])?.map(i => i.andamentos)?.flat()?.length;
    //@ts-ignore
    let contagem_depois_prazo: number = (depois_prazo ?? [])?.map(i => i.andamentos)?.flat()?.length;

    return [
        { label: 'Adiantado', value: contagem_antes_prazo, color: '#FFD700' },
        { label: 'No prazo', value: contagem_dentro_prazo, color: '#29e922' },
        { label: 'Atrasado', value: contagem_depois_prazo, color: '#DC143C' },
    ]?.filter(i => i.value > 0);

    /**estrutura do backend, mas eu não uso. Por enquando eu obtenho as informações direto pelos dados no frontend */
    // return {
    //     prazo: {
    //         area: 0,
    //         dias: 0,
    //         porcentagem: 0,
    //     },
    //     atrasada: {
    //         area: 0,
    //         dias: 0,
    //         porcentagem: 0
    //     },
    //     adiantada: {
    //         area: 0,
    //         dias: 0,
    //         porcentagem: 0
    //     }
    // }
}

//#region GERAR ESTRUTURA LISTA ATIVIDADES
/**Gera a estrutura necessária para montar os cards das atividades */
export function gerarEstruturaAtividades(
    lista_atividades: Item__Atividades[],
    info_emergencia: { dias: number, data: string },
    lista_usuarios: Item__EquipeCliente[],
    lista_propriedades: ItemPropriedades[]
): typeAtvEstrutura[] {

    /**atividades executadas que estão vinculadas a uma atividade planejada */
    let atividades_vinculadas = (lista_atividades ?? []).filter(i => i.cod_atividade !== null);

    /**estrutura dos cards para listar por talhão ou subárea */
    let lista_cards: typeAtvEstrutura[] = (lista_atividades ?? []).filter(i => i.cod_atividade === null).map((card) => {
        let tipo_atv = card?.cod_tipo === 1 ? 'planejada' : 'executada' as 'planejada' | 'executada';
        return {
            cod: card?.cod,
            cod_atividade: card?.cod_atividade,
            cod_referencia: card?.cod_referencia,
            cod_tipo: card?.cod_tipo,
            dias: card?.dias,
            data_prevista: card?.data_inicio,
            data_final_prevista: card?.data_final,
            cod_operacao: card?.cod_operacao,
            titulo_operacao: card?.descricao ?? card?.tipo_operacao,
            qtd_atv_executada: 0,
            is_concluido: card?.concluido ?? false,
            porcentagem_executada: Number(card?.porcentagem_executada),
            finalidades_executadas: card?.cod_tipo === 2 ? obterFinalidadeDireta(card) : obterFinalidadesVinculadas(atividades_vinculadas, card, lista_usuarios, lista_propriedades),
            dados_atividade: card,
            tipo_atividade: tipo_atv,
            resumo_compatibilidades: obterResumosCompatibilidades(card, atividades_vinculadas),
            nome_propriedade: card?.propriedade_nome ?? '',
            cod_propriedade: card?.cod_propriedade ?? null,
            cod_talhao: card?.cod_talhao ?? null,
            listar_primeiro: 1,
            porcentagens: obterPorcentagensDePrazo(card, atividades_vinculadas),
            ocultar_plantio_ref_filtro_avancado: card?.oculto,
            tipo_referencia: card?.tipo_referencia,
            obs: card?.observacao,
            tipo_filtro: card?.tipo_filtro,
            nome_talhao: card?.nome_talhao,
            nome_subarea: card?.nome_subarea,
            nome_subcard: card?.nome_subcard ?? '?'
        }
    });

    /**`boolean` busca na estrutura se possui atividade com `operação plantio` e que seja atividade planejada */
    let possui_plantio: boolean = (lista_cards ?? []).some(i => i.cod_operacao === 100 && i.cod_tipo === 1 && !(i.ocultar_plantio_ref_filtro_avancado));

    //TODO 09/02/26-17h07
    //PRECISA revisar como é feito a inclusão da data de emergência no caso de subareas, pois atualmente está buscando a data de emergencia do subarea, 
    //mas geralmente a data de emergencia da subarea não está cadastrada. Dai está vindo null. Como vem null, está calculando a data de emergencia padrão de 5 dias (data plantio + 5 dias).
    // o problema disso é que é sempre mostrado o valor padrão de 5 dias, mesmo após alterar a data de emergencia do talhão pai
    /**se `true` cria um card para exibir a data de emergência */
    if (possui_plantio) {
        /**obtem a data do plantio */
        let data_prevista_plantio = (lista_cards ?? []).find(i => i.cod_operacao === 100 && i.cod_tipo === 1 && !(i.ocultar_plantio_ref_filtro_avancado))?.data_prevista;
        lista_cards.push({
            cod: -1111,
            cod_atividade: null,
            cod_tipo: null,
            dias: info_emergencia.data ? dayjs(info_emergencia.data).diff(data_prevista_plantio, 'days') : 5,
            data_prevista: info_emergencia.data ?? dayjs(data_prevista_plantio).add(5, 'day').format("YYYY-MM-DD"),
            cod_operacao: null,
            titulo_operacao: "Emergência",
            qtd_atv_executada: 0,
            is_concluido: false,
            porcentagem_executada: 0,
            finalidades_executadas: [],
            dados_atividade: [],
            tipo_atividade: 'emergencia',
            resumo_compatibilidades: null,
            nome_propriedade: '',
            cod_propriedade: null,
            cod_talhao: null,
            porcentagens: [],
            listar_primeiro: 0,
            tipo_filtro: 'sem filtro'
        });
    };

    return orderBy(lista_cards, ['data_prevista', 'listar_primeiro', 'data_final_prevista'], ['asc', 'asc']) ?? [];
};
//#endregion

//#region GERAR ESTRUTURA LISTA ATIVIDADES GERAL
/**Agrupa finalidades de todas as atvs axecutadas da atividade planejada, mas contabilizando todos os talhões e propriedades do cliente (geral) */
function agruparFinalidadesExecutadas(obter_finalidades_exe: any, lista_talhoes: any[]) {

    let agrupar_por_finalidade = groupBy(obter_finalidades_exe, 'nome_finalidade');

    let estrutura_agrupada = (Object.entries(agrupar_por_finalidade) ?? []).map(([nome_finalidade, dados_finalidade]) => {

        let soma_porcentagem = (dados_finalidade ?? []).reduce((acc, item) => acc + Number(item.porcentagem_executada), 0);
        let soma_area_total = (lista_talhoes ?? []).reduce((acc, item) => acc + Number(item.area), 0);
        let soma_area_executada = (dados_finalidade ?? []).reduce((acc, item) => acc + Number(item.area_executada), 0);

        return {
            nome_finalidade: nome_finalidade,
            cod_finalidade: dados_finalidade?.[0]?.cod_finalidade,
            porcentagem_executada: (soma_porcentagem / (100 * lista_talhoes.length)) * 100,
            area_total: soma_area_total,
            area_executada: soma_area_executada,
            //@ts-ignore
            andamentos: dados_finalidade?.map((i) => i.andamentos).flat()
        }
    })


    return estrutura_agrupada ?? [];
}

/**retorna a estrutra da lista de atividades de todas os talhões de todas as propriedades do cliente */
export function gerarEstruturaAtividadesGeral(estrutura_atvs_simples: typeAtvEstrutura[], lista_propriedades: ItemPropriedades[]): typeAtvEstrutura[] {

    /**incluir chave que será usada para conseguir agrupar por operacacao/descricao e dias*/
    let lista_estrutura_simples = (estrutura_atvs_simples ?? []).filter(i => i.tipo_atividade === 'planejada').map((item) => { return { ...item, ref_agrupar: `${item.titulo_operacao}_____${item.dias}` } });
    /**agrupar por operação/descricao e dias */
    let agrupar_por_titulo_e_data_prevista = groupBy(lista_estrutura_simples, 'ref_agrupar');

    let nova_estrutura = (Object.entries(agrupar_por_titulo_e_data_prevista) ?? []).map(([titulo_operacao_agrupada, lista]) => {

        /**quantidade de atividades com o mesmo nome e mesma dias */
        let qtd_atvs = (lista ?? []).length;
        /**porcentagem execitada considerando todas os talhões de todas as propriedades (atvs com msm nome e dias) */
        let soma_porcentagem_executada = (lista ?? []).reduce((acc, item) => acc + item.porcentagem_executada, 0);
        /**checa se a atividade está concluída em todos os talhões de todas as propriedades */
        let is_concluido = (lista ?? []).reduce((acc: boolean, item: any) => acc && item.is_concluido, false);

        /**mesclar atvs executadas da atividades de todas a propri...e talh... em um unico array */
        //@ts-ignore
        let obter_finalidades_exe = lista.map((i) => { return i.finalidades_executadas }).flat();

        /**lista de propriedades (usada para obter os cods ) */
        let propriedades = uniqBy((lista ?? []).map((i, index) => { return { nome_propriedade: i.nome_propriedade, cod_propriedade: i.cod_propriedade, posicao: index } }), 'cod_propriedade');

        /**retorna as informações necessárias sobre o talhão. (listando no card geral)
         * 
         * Essas info não vem diretamente do back, por isso precisa ser obtida apartir dos dados da propriedade */
        function infoTalhao() {

            let talhoes = (lista ?? []).map((i) => i.cod_talhao);
            let cods_propriedades = propriedades.map(i => i.cod_propriedade);
            let propriedades_com_atv = lista_propriedades.filter(i => cods_propriedades.includes(i.cod));

            let talhoes_com_atv = propriedades_com_atv.map(({ cod, nome, talhao }, index) => {
                return talhao.filter((i) => talhoes.includes(i.cod)).map((i) => { return { ...i, cod_propriedade: cod, nome_propriedade: nome, posicao: index } })//nosonar
                // @ts-ignore
            }).flat();

            let lista_talhoes = (talhoes_com_atv ?? []).map((item) => {
                return {
                    nome_propriedade: item?.nome_propriedade ?? '',
                    cod_propriedade: item?.cod_propriedade ?? null,
                    cod_talhao: item?.cod ?? null,
                    nome_talhao: item?.nome ?? '',
                    coordenadas: item?.kml?.coordenadas ?? null,
                    posicao: item?.posicao ?? null,
                    area: item?.area ?? '0'
                }
            })

            return lista_talhoes ?? [];
        };

        /**retorna informações necessárias da propriedade (listando card geral) */
        function infoPropriedades() {

            let agrupar_propriedades = groupBy(infoTalhao(), 'cod_propriedade');

            let list_propriedades = (Object.entries(agrupar_propriedades) ?? []).map(([cod_propriedade, items], index) => {
                return {
                    cod_propriedade: Number(cod_propriedade),
                    nome_propriedade: items?.[0]?.nome_propriedade,
                    posicao: index, //usado para ordenar os talhões da propriedade
                    talhoes: items?.length ?? 0, //quantidade de talhões da propriedade
                }
            })

            return list_propriedades ?? []
        }

        /**retorna a lista de talhões que serão exibidos no card geral */
        function obterTalhoesAgrupados(lista_atvs: any[]) {
            let talhoes_concluidos = (lista_atvs ?? []).filter(i => i.is_concluido)?.map(j => j.cod_talhao);

            let reestrutura_talhoes = (infoTalhao() ?? []).map((item, ix) => {
                let obterPosicao = (infoPropriedades()).findIndex(i => i.cod_propriedade === item?.cod_propriedade);
                return {
                    ...item,
                    posicao: obterPosicao,
                    is_atv_concluida: (talhoes_concluidos ?? []).some(i => i === item?.cod_talhao)
                }
            });
            return reestrutura_talhoes;
        }

        /**retorna lista de incompatibilidades encontrada em qualquer propriedade ou talhão com a atividade informada */
        function obterResumosCompatibilidades(): typeResumoEstrutura {
            //@ts-ignore
            let apenas_lista = (lista ?? []).map((i) => i.resumo_compatibilidades)?.flat();

            const unir_objeto = (apenas_lista ?? []).reduce((acc, item) => {
                acc.resumo_planejadas.push(...(item.resumo_planejadas || []));
                acc.resumo_executadas.push(...(item.resumo_executadas || []));

                if (item.possui_alertas) {
                    acc.possui_alertas = true;
                }

                return acc;
            },
                {
                    resumo_planejadas: [],
                    resumo_executadas: [],
                    possui_alertas: false
                }
            );

            return unir_objeto ?? null;
        };


        /**retorna as observaçções da ativdade de todas as propriedadas e talhões */
        function listaObs() {
            let lista_talhoes = obterTalhoesAgrupados(lista);

            function nomePropriedade(cod_talhao: number) {
                let nome_propriedade = (lista_talhoes ?? [])?.find(i => i.cod_talhao === cod_talhao)?.nome_propriedade ?? '';
                return String(nome_propriedade)?.split(" ")?.map(i => i?.[0])?.join('.');
            }
            function nomeTalhao(cod_talhao: number) {
                return (lista_talhoes ?? [])?.find(i => i.cod_talhao === cod_talhao)?.nome_talhao ?? '';
            }

            let lista_obs = (lista ?? []).filter(i => i.obs).map((i) => `(${nomePropriedade(i.cod_talhao)} | ${nomeTalhao(i.cod_talhao)}) ${i.obs}`);

            return lista_obs?.join(',') ?? '';
        }

        /**estrutura final dos cards */
        return {
            cod_agrupado: lista?.map(i => i?.cod)?.join('-'),
            titulo_operacao: String(titulo_operacao_agrupada)?.split('_____')?.[0] ?? '',
            data_prevista: lista?.[0]?.data_prevista ?? '',
            data_final_prevista: lista?.[0]?.data_final_prevista ?? '',
            dias: String(titulo_operacao_agrupada)?.split('_____')?.[1] ?? '',
            porcentagem_executada: Number((soma_porcentagem_executada / (qtd_atvs * 100)) * 100),
            cod: lista?.[0]?.cod,
            cod_atividade: null,
            cod_referencia: lista?.[0]?.cod_referencia,
            cod_operacao: lista?.[0]?.cod_operacao,
            qtd_atv_executada: 0,
            is_concluido: is_concluido,
            finalidades_executadas: agruparFinalidadesExecutadas(obter_finalidades_exe, obterTalhoesAgrupados(lista)),
            dados_atividade: lista,
            tipo_atividade: 'planejada' as "planejada" | "executada",
            resumo_compatibilidades: obterResumosCompatibilidades(),
            cod_propriedade: null,
            propriedades: infoPropriedades(),
            talhoes: obterTalhoesAgrupados(lista),
            // talhoes: reestrutura_talhoes,
            //@ts-ignore
            porcentagens: (lista ?? [])?.map(i => i.porcentagens)?.flat(),
            ocultar_plantio_ref_filtro_avancado: lista?.[0]?.ocultar_plantio_ref_filtro_avancado,
            tipo_referencia: lista?.[0]?.tipo_referencia,
            obs: listaObs(),
            tipo_filtro: lista?.[0]?.tipo_filtro
        }
    });

    return nova_estrutura ?? []
};
//#endregion

//#region TITULO MODAL CADASTRO
/**titulo da gaveta ao clicar em cadastrar ou editar atividade */
export function tituloModalCadastroAtv(tipo_cadastro: 'novo' | 'editar_planejada' | 'vincular_planejada' | 'editar_executada' | 'vincular_executada' | 'vincular_finalidade') {
    switch (tipo_cadastro) {
        case 'novo':
            return 'Cadastrar atividade';

        case 'editar_planejada':
            return 'Ver/Editar atividade planejada';

        case 'vincular_planejada':
            return 'Cadastrar atividade vinculada';

        case 'editar_executada':
            return 'Editar atividade executada';

        case 'vincular_executada':
            return 'Cadastrar atividade executada';

        case 'vincular_finalidade':
            return 'Cadastrar atividade executada';


        default:
            return 'Cadastrar atividade';
    }
}
//#endregion

//#region TOOLTIP
// export function titleTooltip(diferenca_ate_dia_zero:number, data_picker:string) {

//     let dia_apos_dia_zero:'+'|'' = diferenca_ate_dia_zero > 0 ? '+' :'';
//     let dias_ate_dia_zero:string = String(diferenca_ate_dia_zero);
//     let dia_e_mes:string         = dayjs(data_picker).format('DD/MM');
//     let is_hoje:'hoje'|''        = dayjs(data_picker).format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD') ? 'hoje' : '';

//     return `(${dia_apos_dia_zero}${dias_ate_dia_zero} D) [${dia_e_mes}] - Add atividade ${is_hoje}`;
// };
//#endregion

//#region TIPO DE CARD ATVs
/**retorna o tipo do card. 
 * 
 * `talhao` quando estiver filtrando por talhão ou subarea. exibe apenas as informações da atividade
 * 
 * `propridade` exibe no card lista de talhões
 * 
 * `geral` exibe no card lista de propriedades e talhões
 */
export function tipoCard(cod_propriedade: number, cod_talhao: number, cod_subarea: number): 'geral' | 'propriedade' | 'talhao' {

    let cod_talhao_ou_subarea = cod_talhao ?? cod_subarea;

    if (Number(cod_propriedade) > 0 && Number(cod_talhao_ou_subarea) > 0) {
        return 'talhao';
    }
    else if (Number(cod_propriedade) > 0 && Number(cod_talhao_ou_subarea) <= 0) {
        return 'propriedade';
    }

    return 'geral'
}
//#endregion

//#region QUANTIDADE CARDS
/**retorna a quantidade de atividades (cards) usado par floataction*/
export function qtdCards(estr_atv: typeAtvEstrutura[]) {
    return leadZero(((estr_atv ?? []).filter(i => i.cod > 0))?.length ?? 0);
}
//#endregion
